import { useMemo } from 'react'
import { createAvatar } from '@dicebear/core'
import { adventurer, bottts, funEmoji, lorelei, pixelArt, thumbs } from '@dicebear/collection'

// Map of style IDs to their DiceBear modules
const STYLE_MAP = {
  adventurer,
  bottts,
  funEmoji,
  lorelei,
  pixelArt,
  thumbs,
} as const

export type AvatarStyleId = keyof typeof STYLE_MAP

interface AvatarConfig {
  style: AvatarStyleId
  seed: string
  options?: Record<string, string | number>
}

interface DiceBearAvatarProps {
  config: string | undefined  // JSON string of { style, seed, options? }
  fallbackSeed?: string       // Fallback seed if no config (e.g., child's name)
  size?: number
  className?: string
}

// Parse the avatar config from JSON string
function parseConfig(configStr: string | undefined, fallbackSeed: string): AvatarConfig {
  if (!configStr) {
    return { style: 'adventurer', seed: fallbackSeed }
  }
  
  try {
    const parsed = JSON.parse(configStr)
    // Handle new DiceBear format with style, seed, and options
    if (parsed.style && parsed.seed) {
      return {
        style: parsed.style in STYLE_MAP ? parsed.style : 'adventurer',
        seed: parsed.seed,
        options: parsed.options || {}
      }
    }
    // Old format - use fallback
    return { style: 'adventurer', seed: fallbackSeed }
  } catch {
    return { style: 'adventurer', seed: fallbackSeed }
  }
}

// Convert options for DiceBear (wrap strings in arrays)
function convertOptions(options: Record<string, string | number>): Record<string, unknown> {
  const result: Record<string, unknown> = {}
  for (const [key, value] of Object.entries(options)) {
    if (key.endsWith('Probability')) {
      result[key] = value
    } else if (typeof value === 'string') {
      result[key] = [value]
    } else {
      result[key] = value
    }
  }
  return result
}

export default function DiceBearAvatar({ 
  config, 
  fallbackSeed = 'default',
  size = 128,
  className = ''
}: DiceBearAvatarProps) {
  const avatarUri = useMemo(() => {
    const { style, seed, options = {} } = parseConfig(config, fallbackSeed)
    const styleModule = STYLE_MAP[style]
    const dicebearOptions = convertOptions(options)
    
    if (!styleModule) {
      return createAvatar(adventurer, { seed, size }).toDataUri()
    }
    
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    return createAvatar(styleModule as any, { seed, size, ...dicebearOptions }).toDataUri()
  }, [config, fallbackSeed, size])

  return (
    <img 
      src={avatarUri} 
      alt="Avatar" 
      className={className}
      style={{ width: size, height: size }}
    />
  )
}

// Export helper to generate URI directly (for cases where we need just the URI)
export function generateAvatarUri(config: string | undefined, fallbackSeed: string, size = 128): string {
  const { style, seed, options = {} } = parseConfig(config, fallbackSeed)
  const styleModule = STYLE_MAP[style] || adventurer
  const dicebearOptions = convertOptions(options)
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  return createAvatar(styleModule as any, { seed, size, ...dicebearOptions }).toDataUri()
}










